import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StatusService } from '../../../services/status.service';
import { 
  Status, 
  StatusCreate, 
  StatusUpdate,
  ENTITY_TYPES
} from '../../../models/Status';

@Component({
  selector: 'app-status-management-form',
  templateUrl: './status-management-form.component.html',
  styleUrls: ['./status-management-form.component.css']
})
export class StatusManagementFormComponent implements OnInit {
  form!: FormGroup;
  loading: boolean = false;
  saving: boolean = false;
  error: string = '';
  isEditMode: boolean = false;
  isViewMode: boolean = false;
  statusId: number | null = null;
  
  // Dados para selects
  entityTypes = ENTITY_TYPES;

  constructor(
    private fb: FormBuilder,
    private statusService: StatusService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadRouteData();
  }

  /**
   * Inicializa o formulário
   * IMPORTANTE: O campo 'code' não é incluído pois é gerado automaticamente pelo backend
   * Campos removidos: isDefault e displayOrder (ordenação agora é alfabética)
   */
  initForm(): void {
    this.form = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      entityType: ['', Validators.required],
      isActive: [true]
    });
  }

  /**
   * Carrega dados da rota
   */
  loadRouteData(): void {
    const id = this.route.snapshot.paramMap.get('id');
    const path = this.route.snapshot.url[this.route.snapshot.url.length - 1]?.path;
    
    this.isViewMode = path === 'view';
    
    if (id && id !== 'new') {
      this.isEditMode = true;
      this.statusId = parseInt(id, 10);
      this.loadStatus(this.statusId);
    }

    if (this.isViewMode) {
      this.form.disable();
    }
  }

  /**
   * Carrega um status existente
   */
  loadStatus(id: number): void {
    this.loading = true;
    this.error = '';

    this.statusService.getById(id).subscribe({
      next: (status) => {
        this.form.patchValue({
          name: status.name,
          description: status.description || '',
          entityType: status.entityType,
          isActive: status.isActive !== false
        });
        
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Erro ao carregar status';
        console.error('Erro ao carregar status:', err);
        this.loading = false;
      }
    });
  }


  /**
   * Submeter formulário
   */
  onSubmit(): void {
    if (this.form.invalid) {
      this.markFormGroupTouched(this.form);
      return;
    }

    this.saving = true;
    this.error = '';

    const formValue = this.form.value;

    if (this.isEditMode && this.statusId) {
      // Atualizar - novo formato da API requer name, entityType e description
      const updateData: StatusUpdate = {
        name: formValue.name,
        entityType: formValue.entityType,
        description: formValue.description
      };
      
      this.statusService.update(this.statusId, updateData).subscribe({
        next: (updatedStatus) => {
          this.saving = false;
          // Mostrar mensagem sobre código regenerado se necessário
          if (updatedStatus.code) {
          }
          this.router.navigate(['/config/status']);
        },
        error: (err) => {
          this.error = 'Erro ao atualizar status';
          console.error('Erro ao atualizar status:', err);
          this.saving = false;
        }
      });
    } else {
      // Criar
      const statusData: StatusCreate = {
        name: formValue.name,
        description: formValue.description,
        entityType: formValue.entityType,
        isActive: formValue.isActive
      };
      
      this.statusService.create(statusData).subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/config/status']);
        },
        error: (err) => {
          this.error = 'Erro ao criar status';
          console.error('Erro ao criar status:', err);
          this.saving = false;
        }
      });
    }
  }

  /**
   * Marca todos os campos como touched para mostrar erros
   */
  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Cancelar e voltar
   */
  cancel(): void {
    this.router.navigate(['/config/status']);
  }

  /**
   * Editar (quando em modo visualização)
   */
  edit(): void {
    if (this.statusId) {
      this.router.navigate(['/config/status', this.statusId, 'edit']);
    }
  }

  /**
   * Verifica se campo tem erro
   */
  hasError(field: string): boolean {
    const control = this.form.get(field);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /**
   * Obtém mensagem de erro do campo
   */
  getErrorMessage(field: string): string {
    const control = this.form.get(field);
    
    if (control?.hasError('required')) {
      return 'Campo obrigatório';
    }
    
    if (control?.hasError('pattern')) {
      return 'Use apenas letras maiúsculas, números e underscore';
    }
    
    return '';
  }

}
